import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Form, Image } from 'semantic-ui-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ControlledTextFormField,
  PasswordFormField,
  ShadowBox,
  StyledButton,
} from '../elements';
import { InvitesService, ToastMessage } from '../utility';
import { UserService } from '../utility/services/user';
import { LoaderBar } from '../components';
import { Images } from '../images';
import {
  PASSWORD_LENGTH_MESSAGE,
  PASSWORD_MIN_LENGTH,
  PASSWORD_PATTERNS,
  describeInviteCheck,
  describeJoinError,
} from './accept-invite.helpers';

const LoginContainer = styled.div`
  position: relative;
  z-index: 2;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const PageContainer = styled.div`
  background: ${({ theme }) => theme.colors.box};
  display: flex;
  min-height: 100vh;
  justify-content: center;
  flex-direction: column;
`;

const Box = styled(ShadowBox)`
  padding: 3em 2em;
  width: 432px;
`;

const LoginText = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  font-size: 20px;
  line-height: 23px;
  margin-bottom: 1.5em;
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.danger};
  margin-bottom: 1.5em;
  font-weight: bold;
  font-size: 15px;
  line-height: 18px;
`;

const SuccessText = styled.div`
  color: ${({ theme }) => theme.colors.success};
  margin-bottom: 1.5em;
  font-weight: bold;
  font-size: 15px;
  line-height: 18px;
`;

const Logo = styled(Image)`
  height: auto;
  width: 450px;
  margin: 0 auto;
`;

const LogoContainer = styled(Link)`
  margin: 50px auto 100px;
  z-index: 9;
`;

const LoginLinkStyle = styled.div`
  p {
    color: #acadb6;
    font-size: 1em;
    margin: 2em 0 0;

    a {
      color: #393948;
    }
  }
`;

const AcceptInviteSchema = yup.object().shape({
  firstName: yup.string().trim().required('First Name is required'),
  lastName: yup.string().trim().required('Last Name is required'),
  password: PASSWORD_PATTERNS.reduce(
    (schema, [pattern, message]) => schema.matches(pattern, message),
    yup
      .string()
      .required(PASSWORD_LENGTH_MESSAGE)
      .min(PASSWORD_MIN_LENGTH, PASSWORD_LENGTH_MESSAGE),
  ),
  confirmPassword: yup
    .string()
    .required()
    .oneOf([yup.ref('password'), null], 'Passwords must match'),
});

export function AcceptInviteScreen() {
  const [searchQuery] = useSearchParams();
  const token = searchQuery.get('token');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [valid, setValid] = useState(false);
  const [orgName, setOrgName] = useState('');
  // EN-4052: an existing account joins with an explicit click, signed in as the invited email.
  const [join, setJoin] = useState(null);
  const [joined, setJoined] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(AcceptInviteSchema),
    shouldUnregister: true,
  });

  useEffect(() => {
    const validateToken = async () => {
      try {
        setLoading(true);
        const result = await InvitesService.check(token);
        const signedInEmail = UserService.getUserInfo()?.email || '';
        const view = describeInviteCheck(result, signedInEmail);
        if (view.orgName) {
          setOrgName(view.orgName);
        }
        if (view.kind === 'join') {
          setValid(false);
          setJoin({ message: view.message, email: result.email });
        } else if (view.kind === 'existing') {
          setValid(false);
          setSuccess(view.message);
        } else if (view.kind === 'new') {
          reset({
            firstName: view.firstName,
            lastName: view.lastName,
          });
          setValid(true);
        } else {
          setValid(false);
          setError(view.message);
        }
      } catch (error) {
        ToastMessage.apiError(error);
        setValid(false);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      validateToken();
    }
  }, [token]);

  const onSubmit = async data => {
    try {
      setError('');
      setSaving(true);
      const paylaod = {
        ...data,
      };
      delete paylaod.confirmPassword;
      await InvitesService.accept(token, paylaod);
      setSuccess('Password set successfully. Login to use the application');
    } catch (error) {
      setError('Invalid email and/or password is invalid');
      setSuccess('');
    } finally {
      setSaving(false);
    }
  };

  const onJoin = async () => {
    try {
      setError('');
      setSaving(true);
      await InvitesService.accept(token, {});
      setJoined(true);
      setSuccess(
        `You have joined ${orgName || 'the organization'}. Switch to it from the organization menu.`,
      );
    } catch (joinError) {
      setError(describeJoinError(joinError, join?.email));
    } finally {
      setJoin(null);
      setSaving(false);
    }
  };

  return (
    <>
      <PageContainer>
        <LogoContainer to="/login">
          <Logo src={Images.blackLogo} />
        </LogoContainer>
        <LoginContainer>
          <Box className="position-relative">
            {(loading || saving) && (
              <LoaderBar content={loading ? 'Loading' : 'Saving'} />
            )}
            <LoginText>Accept Invitation from {orgName}</LoginText>

            {success ? (
              <>
                <SuccessText>{success}</SuccessText>
                <Link to={joined ? '/app' : '/login'}>
                  <StyledButton fluid>{joined ? 'Continue' : 'Go to Login'}</StyledButton>
                </Link>
              </>
            ) : join ? (
              <>
                <SuccessText>{join.message}</SuccessText>
                <StyledButton fluid onClick={onJoin} disabled={saving}>
                  Join {orgName || 'the organization'}
                </StyledButton>
              </>
            ) : (
              <>
                {valid ? (
                  <Form onSubmit={handleSubmit(onSubmit)}>
                    <ControlledTextFormField
                      error={errors.firstName}
                      hint={errors.firstName?.message}
                      label="First Name"
                      name="firstName"
                      control={control}
                      required
                    />
                    <ControlledTextFormField
                      error={errors.lastName}
                      hint={errors.lastName?.message}
                      label="Last Name"
                      name="lastName"
                      control={control}
                      required
                    />

                    <PasswordFormField
                      error={errors.password}
                      label="Create a Password"
                      name="password"
                      control={control}
                      required
                      hint={errors?.password?.message}
                    />

                    <PasswordFormField
                      error={errors.confirmPassword}
                      label="Confirm Password"
                      name="confirmPassword"
                      control={control}
                      required
                    />

                    <StyledButton fluid type="submit">
                      Create Account
                    </StyledButton>

                    <LoginLinkStyle>
                      <p>
                        Already on SKUPREME? <Link to="/login">Log In</Link>
                      </p>
                    </LoginLinkStyle>
                  </Form>
                ) : (
                  <>
                    <ErrorText>{error}</ErrorText>
                    <Link to="/login">
                      <StyledButton fluid>Go to Login</StyledButton>
                    </Link>
                  </>
                )}
              </>
            )}
          </Box>
        </LoginContainer>
      </PageContainer>
    </>
  );
}
